import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { supabase } from '../../services/supabase';
import { isDemoAccountSession } from './creatorSession';

/**
 * The creator's two sponsor switches, shown side by side in My Page and in
 * Settings. The creator Terms promise both can be turned off in the dashboard.
 *
 * SPONSOR ADS (`talent_profiles.auto_apply_sponsors`). Every write goes
 * through `set_sponsor_ads()`, never a direct update. For a creator who has
 * never chosen a sponsored fan email setting (`sponsored_fan_emails_enabled`
 * null), the database falls back to this same flag, so a direct flip would
 * quietly change their fan emails as well. `set_sponsor_ads()` first saves
 * that setting at the value it already had, in the same statement, so the
 * switch only ever changes the ads on the page. Turning ads on places no card
 * straight away: the nightly sponsor run picks the first one, hence "within a
 * day".
 *
 * SPONSORED EMAILS TO YOUR FANS (`sponsored_fan_emails_enabled`). Read with
 * `get_sponsored_fan_emails()`, which returns the EFFECTIVE setting
 * (`sponsored_fan_emails_active()`), so a creator who never chose sees it on
 * while their ads are on. Written with `set_sponsored_fan_emails()`, which
 * stores the choice explicitly; the database logs every change.
 *
 * LOCKED. Staff can take a page off sponsors (`private.sponsor_bans`, 27 Oct
 * 2026). Both switches then show a neutral locked state: no switch, just
 * "not available", never the reason. The database enforces it on its own:
 * both writes refuse to turn anything on (hint `sponsor_banned`), and that
 * refusal locks the switch here too.
 */

export const SPONSOR_ADS_TITLE = 'Sponsor ads on your page';
export const SPONSOR_ADS_DESCRIPTION =
  'You earn a share each time a visitor clicks one. While they’re off, you don’t earn ad money.';

export const FAN_EMAILS_TITLE = 'Sponsored emails to your fans';
export const FAN_EMAILS_DESCRIPTION =
  'Now and then we email your fans a sponsored offer, clearly labeled as an ad. You earn a share of each click. While it’s off, your fans don’t get them.';

// DRAFT copy (Nicky to confirm).
export const SPONSOR_ADS_LOCKED =
  'Sponsor ads aren’t available for your page. Contact support if you have questions.';
export const FAN_EMAILS_LOCKED =
  'Sponsored emails aren’t available for your page. Contact support if you have questions.';

/** The server's refusal when staff have taken the page off sponsors. */
export function isSponsorLockedError(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { hint?: unknown }).hint === 'sponsor_banned';
}

/**
 * Whether staff have taken this page off sponsors. Unlocked on any error: the
 * server refuses the switch regardless, and the refusal locks it then.
 */
export async function readSponsorLocked(talentId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('creator_sponsor_ban_state', { p_talent_id: talentId });
    return !error && (data as { locked?: unknown } | null)?.locked === true;
  } catch {
    return false;
  }
}

/** Says why a locked switch does nothing. */
export function announceSponsorLocked(message: string): void {
  toast(message, { id: 'sponsor-locked' });
}

export async function setSponsorAds(talentId: string, on: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_sponsor_ads', { p_talent_id: talentId, p_on: on });
  if (error) throw error;
}

export function announceSponsorAds(on: boolean): void {
  toast.success(
    on
      ? 'Sponsor ads are on. Your first sponsor will appear within a day.'
      : 'Sponsor ads are off. You won’t earn ad money while they’re off.'
  );
}

export type SponsorSwitch = {
  /** The switch applies to this account (e.g. not the review account). */
  available: boolean;
  /** The creator's setting; null until it has loaded, or if the read failed. */
  on: boolean | null;
  /** The last read failed: show a retry (reload) rather than a dead switch. */
  failed: boolean;
  /** Staff took the page off sponsors: show `lockedMessage`, no switch. */
  locked: boolean;
  lockedMessage: string;
  busy: boolean;
  toggle: (next: boolean) => Promise<void>;
  /** Re-read after something else (Pre-paid Sponsors) changed the setting. */
  reload: () => void;
};

/**
 * One server-backed switch: read on mount and on reload(), optimistic toggle
 * that reverts and says so when the write fails.
 */
function useServerSwitch(
  talentId: string | null | undefined,
  read: (talentId: string) => Promise<{ available: boolean; on: boolean | null; locked: boolean }>,
  write: (talentId: string, on: boolean) => Promise<void>,
  announce: (on: boolean) => void,
  failure: string,
  lockedMessage: string,
  onChanged?: (on: boolean) => void
): SponsorSwitch {
  const readFn = useRef(read);
  readFn.current = read;
  const writeFn = useRef(write);
  writeFn.current = write;
  const announceFn = useRef(announce);
  announceFn.current = announce;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const [available, setAvailable] = useState(false);
  const [on, setOn] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);
  const [locked, setLocked] = useState(false);
  const lockedRef = useRef(false);
  lockedRef.current = locked;
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);
  // A read that started before a toggle must not overwrite the toggle's result.
  const readRef = useRef(0);

  useEffect(() => {
    if (!talentId) return;
    const run = ++readRef.current;
    setFailed(false);
    (async () => {
      try {
        const result = await readFn.current(talentId);
        if (run !== readRef.current) return;
        setAvailable(result.available);
        setLocked(result.locked);
        setOn(result.locked ? false : result.on);
        setFailed(!result.locked && result.on === null);
      } catch {
        if (run !== readRef.current) return;
        setOn(null);
        setFailed(true);
      }
    })();
  }, [talentId, nonce]);

  const toggle = useCallback(
    async (next: boolean) => {
      if (!talentId) return;
      if (lockedRef.current) {
        announceSponsorLocked(lockedMessage);
        return;
      }
      readRef.current++;
      setBusy(true);
      setOn(next);
      try {
        await writeFn.current(talentId, next);
        announceFn.current(next);
        onChangedRef.current?.(next);
      } catch (err) {
        if (isSponsorLockedError(err)) {
          setLocked(true);
          setOn(false);
          announceSponsorLocked(lockedMessage);
        } else {
          setOn(!next);
          toast.error(failure);
        }
      } finally {
        setBusy(false);
      }
    },
    [talentId, failure, lockedMessage]
  );

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { available, on, failed, locked, lockedMessage, busy, toggle, reload };
}

async function readSponsorAds(talentId: string) {
  const [flag, row, locked] = await Promise.all([
    supabase.from('platform_settings').select('setting_value').eq('setting_key', 'sponsor_ads_enabled').maybeSingle(),
    supabase.from('talent_profiles').select('auto_apply_sponsors').eq('id', talentId).maybeSingle(),
    readSponsorLocked(talentId),
  ]);
  if (flag.error) throw flag.error;
  return {
    available: (flag.data as any)?.setting_value === 'true' && !isDemoAccountSession(),
    on: row.error || !row.data ? null : !!(row.data as any).auto_apply_sponsors,
    locked,
  };
}

export function useSponsorAdsSwitch(
  talentId: string | null | undefined,
  /** After a successful change, e.g. to refresh a page preview. */
  onChanged?: (on: boolean) => void
): SponsorSwitch {
  return useServerSwitch(
    talentId,
    readSponsorAds,
    setSponsorAds,
    announceSponsorAds,
    'Couldn’t change sponsor ads. Try again.',
    SPONSOR_ADS_LOCKED,
    onChanged
  );
}

export async function setSponsoredFanEmails(talentId: string, on: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_sponsored_fan_emails', { p_talent_id: talentId, p_on: on });
  if (error) throw error;
}

async function readSponsoredFanEmails(talentId: string) {
  const [{ data, error }, locked] = await Promise.all([
    supabase.rpc('get_sponsored_fan_emails', { p_talent_id: talentId }),
    readSponsorLocked(talentId),
  ]);
  return {
    // The review account never sees the paid-sponsor surface.
    available: !isDemoAccountSession(),
    on: error || typeof data !== 'boolean' ? null : data,
    locked,
  };
}

function announceSponsoredFanEmails(on: boolean): void {
  toast.success(
    on
      ? 'Sponsored emails to your fans are on.'
      : 'Sponsored emails to your fans are off. Your fans won’t get them.'
  );
}

export function useSponsoredFanEmailsSwitch(talentId: string | null | undefined): SponsorSwitch {
  return useServerSwitch(
    talentId,
    readSponsoredFanEmails,
    setSponsoredFanEmails,
    announceSponsoredFanEmails,
    'Couldn’t change sponsored emails. Try again.',
    FAN_EMAILS_LOCKED
  );
}
